import React, { useState, useEffect, useRef, useContext } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Toolbar } from 'primereact/toolbar'
import { Button } from 'primereact/button'

import server from '../../../server'
import { useLocation, useNavigate } from 'react-router-dom'
import MainContentLayout from '../../Layout/MainContentLayout'

import {
  InputFieldTemplate,
  MultiSelectTemplate,
} from '../../shared/FilterTemplates'

import ModifyMolecule from './ModifyMolecule'
import { Toast } from 'primereact/toast'
import { ToastPopUp } from '../../shared/utils'

import { AuthenticationContext } from '../../../Auth/authentication.context'
import useTableRowsPerPage from '../../../hooks/useTableRowsPerPage'
import { getInitialPaginationState } from '../../../shared/tablePagination'

export default function MoleculesTable() {
  const { user } = useContext(AuthenticationContext)
  const { defaultRows, rowsPerPageOptions, applyPageEvent } = useTableRowsPerPage()
  const [molecules, setMolecules] = useState(null)
  const [allMolecules, setAllMolecules] = useState(null) // Store all molecules for client-side filtering
  const [selectedMolecule, setSelectedMolecule] = useState(null)
  const [totalRecords, setTotalRecords] = useState(0)
  const [tableLoading, setTableLoading] = useState(false)
  const [csvLoading, setcsvLoading] = useState(false)

  const [clearBtnDisabled, setClearBtnDisabled] = useState(true)
  const [lazyState, setLazyState] = useState(() =>
    getInitialPaginationState(user, {
      sortField: '',
      sortOrder: '',
      filters: {
        source: { value: '' },
        drugIdentificationNo: { value: '' },
        manufacturers: { value: '' },
        medications: { value: '' },
        upcs: { value: '' },
      },
    })
  )

  const initLazyState = () => {
    setLazyState((prevState) => ({
      ...prevState,
      first: 0,
      page: 0,
      rows: defaultRows,
      sortField: '',
      sortOrder: '',
      filters: {
        source: { value: '' },
        drugIdentificationNo: { value: '' },
        manufacturers: { value: '' },
        medications: { value: '' },
        upcs: { value: '' },
      },
    }))
  }

  const navigate = useNavigate()

  const dt = useRef()
  const toast = useRef()
  const { state } = useLocation()
  const AddEditRef = useRef()
  const DeleteRef = useRef()
  const refs = { AddEditRef, DeleteRef }

  useEffect(() => {
    setLazyState((prev) => ({ ...prev, rows: defaultRows }))
  }, [defaultRows])

  // Gets table data (server-side filters for DIN, source, upc, medication, manufacturer)
  const getData = () => {
    const moleculeSource = lazyState?.filters?.source.value
    const drugIdentificationNo = lazyState?.filters?.drugIdentificationNo.value
    const upcFilter = lazyState?.filters?.upcs?.value
    const medicationFilter = lazyState?.filters?.medications?.value
    const manufacturerFilter = lazyState?.filters?.manufacturers?.value

    const params = new URLSearchParams()
    params.set('page', lazyState?.page != null ? lazyState.page + 1 : 1)
    params.set('limit', lazyState?.rows ?? defaultRows)
    if (drugIdentificationNo) params.set('drugIdentificationNo', drugIdentificationNo)
    if (moleculeSource?.length) moleculeSource.forEach((item) => params.append('source', item.code))
    if (upcFilter) params.set('upc', upcFilter)
    if (medicationFilter) params.set('medication', medicationFilter)
    if (manufacturerFilter) params.set('manufacturer', manufacturerFilter)
    if (lazyState?.sortField) params.set('sortByField', lazyState.sortField)
    if (lazyState?.sortOrder) params.set('sortByOrder', lazyState.sortOrder)

    setTableLoading(true)

    server
      .get(`/molecules?${params.toString()}`)
      .then((response) => {
        const molecules = response.data.molecules
        setAllMolecules(molecules)
        setMolecules(molecules)
        setTotalRecords(response.data.totalCount)
        setTableLoading(false)
      })
      .catch((error) => {
        setTableLoading(false)
      })
  }
  const exportCSV = () => {
    const moleculeSource = lazyState?.filters?.source.value
    const drugIdentificationNo = lazyState?.filters?.drugIdentificationNo.value
    const upcFilter = lazyState?.filters?.upcs?.value
    const medicationFilter = lazyState?.filters?.medications?.value
    const manufacturerFilter = lazyState?.filters?.manufacturers?.value
    const params = new URLSearchParams()
    if (drugIdentificationNo) params.set('drugIdentificationNo', drugIdentificationNo)
    if (moleculeSource?.length) moleculeSource.forEach((item) => params.append('source', item.code))
    if (upcFilter) params.set('upc', upcFilter)
    if (medicationFilter) params.set('medication', medicationFilter)
    if (manufacturerFilter) params.set('manufacturer', manufacturerFilter)
    if (lazyState?.sortField) params.set('sortByField', lazyState.sortField)
    if (lazyState?.sortOrder) params.set('sortByOrder', lazyState.sortOrder)
    const requestParams = params.toString() ? '?' + params.toString() : ''
    setcsvLoading(true)
    server
      .get(`/molecules/export${requestParams}`, {
        responseType: 'blob',
      })
      .then((response) => {
        const blobData = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const url = window.URL.createObjectURL(blobData)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'molecule.xlsx')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        setcsvLoading(false)
      })
      .catch((error) => {
        setTableLoading(false)
  
      })
  }

  useEffect(() => {
    checkClearBtn()
    // All filtering (including UPC, medication, manufacturer) is done server-side
    getData()
  }, [lazyState])

  // Toast to show validation of deleted molecule ( delete action took place inside molecule History)
  useEffect(() => {
    state &&
      ToastPopUp(
        toast,
        'success',
        'Molecule Deleted',
        'Molecule has been Deleted successfully.'
      )
    window.history.replaceState({}, document.title)
  }, [state])

  // Handles table page change
  const onPage = (event) => {
    setLazyState((prev) => applyPageEvent(prev, event))
  }

  // Handles table sorting
  const onSort = (event) => {
    setLazyState(event)
  }

  // Handles table filtring
  const onFilter = (event) => {
    setLazyState(event)
  }

  // Enable and disable clear button according to filtring and sorting
  const checkClearBtn = () => {
    if (lazyState?.sortField || lazyState?.sortOrder) {
      setClearBtnDisabled(false)
      return
    }
    for (const key in lazyState?.filters) {
      if (lazyState?.filters[key].value) {
        setClearBtnDisabled(false)
        return
      }
    }
    setClearBtnDisabled(true)
  }

  // Handles row selection action (redirect to molecule history table)
  const onRowSelect = (event) => {
    const molecule = molecules.find(
      (molecule) =>
        molecule.drugIdentificationNo === event.data.drugIdentificationNo
    )
    navigate(`/admin/reports/molecules/${event.data.drugIdentificationNo}`, {
      state: molecule,
    })
  }

  // Filter TEMPLATES
  const moleculeSourceFilterElement = (options) => (
    <MultiSelectTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='Molecule source'
      options={[
        { name: 'Single', code: 'single' },
        { name: 'Dual', code: 'dual' },
        { name: 'Multi', code: 'multi' },
      ]}
    />
  )

  const dinFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='DIN'
    />
  )

  const manufacturersFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='Filter by manufacturer'
    />
  )

  const medicationsFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='Filter by medication'
    />
  )

  const upcsFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='Filter by UPC'
    />
  )

  // (Edit - Delete) column body template
  const actionBodyTemplate = (rowData) => {
    return (
      <React.Fragment>
        <div className='d-flex justify-content-center gap-2'>
          <Button
            icon='pi pi-pencil'
            rounded
            outlined
            className='mr-2'
            onClick={() => AddEditRef.current.editMolecule(rowData)}
          />

          <Button
            icon='pi pi-trash'
            rounded
            outlined
            severity='danger'
            onClick={() => DeleteRef.current.confirmDeleteMolecule(rowData)}
          />
        </div>
      </React.Fragment>
    )
  }

  // Toolbar body templates
  const topLeftToolbarTemplate = () => {
    return (
      <div className='d-flex'>
        <Button
          type='button'
          icon='pi pi-filter-slash'
          label='Clear'
          outlined
          onClick={initLazyState}
          disabled={clearBtnDisabled}
        />
      </div>
    )
  }

  const topRightToolbarTemplate = () => {
    return (
      <div className='d-flex'>
        <Button
          label='New molecule'
          icon='pi pi-plus'
          severity='success'
          onClick={() => AddEditRef.current.openNew()}
        />
      </div>
    )
  }

  return (
    <>
      <div className='card flex justify-content-center'>
        <Toast ref={toast} />
      </div>

      <MainContentLayout
        title='Molecules'
        dt={exportCSV}
        handleExport={true}
        loading={csvLoading}
      >
        <div className='card'>
          <Toolbar
            className='mb-2'
            left={topLeftToolbarTemplate}
            right={topRightToolbarTemplate}
          />

          <DataTable
            ref={dt}
            value={molecules}
            paginator
            lazy={true}
            showGridlines
            removableSort
            rowsPerPageOptions={rowsPerPageOptions}
            totalRecords={totalRecords}
            loading={tableLoading}
            dataKey='drugIdentificationNo'
            globalFilterFields={['drugIdentificationNo', 'source']}
            selectionMode='single'
            onRowSelect={onRowSelect}
            selection={selectedMolecule}
            onSelectionChange={(e) => setSelectedMolecule(e.value)}
            emptyMessage='No molecules found.'
            filters={lazyState?.filters}
            sortField={lazyState?.sortField}
            sortOrder={lazyState?.sortOrder}
            rows={lazyState?.rows}
            first={lazyState?.first}
            onPage={onPage}
            onSort={onSort}
            onFilter={onFilter}
          >
            {/* <Column selectionMode='multiple' exportable={false}></Column> */}
            <Column
              field='name'
              header='Molecule Name'
              sortable
              style={{ minWidth: '20rem' }}
              body={(rowData) => {
                return rowData.name || 'N/A'
              }}
            ></Column>
            <Column
              field='drugIdentificationNo'
              header='Din'
              sortable
              style={{ minWidth: '16rem' }}
              filter
              filterElement={dinFilterElement}
              showFilterMatchModes={false}
              showFilterOperator={false}
              showAddButton={false}
              body={(rowData) => {
                const din = rowData.drugIdentificationNo
                if (din == null || din === '') return din
                return String(din).padStart(8, '0')
              }}
            ></Column>
            <Column
              field='source'
              header='Source Type'
              sortable
              style={{ minWidth: '12rem' }}
              filter
              filterElement={moleculeSourceFilterElement}
              showFilterMatchModes={false}
              showFilterOperator={false}
              showAddButton={false}
              body={(rowData) => {
                const source = rowData.source || ''
                if (!source) return source
                // Format: multi -> Multi-Sourced, single -> Single-Sourced, dual -> Dual-Sourced
                const capitalized = source.charAt(0).toUpperCase() + source.slice(1)
                return `${capitalized}-Sourced`
              }}
            ></Column>
            <Column
              field='manufacturers'
              header='Manufacturers'
              style={{ minWidth: '20rem' }}
              filter
              filterElement={manufacturersFilterElement}
              showFilterMatchModes={false}
              showFilterOperator={false}
              showAddButton={false}
              filterMatchMode='custom'
              filterFunction={(value, rowData) => {
                if (!value || value === '') return true
                const filterValue = String(value).toLowerCase().trim()
                if (!filterValue) return true
                const manufacturers = rowData.manufacturers || []
                if (manufacturers.length === 0) return false
                return manufacturers.some(manufacturer => {
                  if (!manufacturer) return false
                  const manufacturerString = String(manufacturer).toLowerCase()
                  return manufacturerString.includes(filterValue)
                })
              }}
              body={(rowData) => {
                const manufacturers = rowData.manufacturers || [];
                if (manufacturers.length === 0) {
                  return <span className='text-muted'>No manufacturers</span>;
                }
                if (manufacturers.length <= 3) {
                  return manufacturers.join(', ');
                }
                return (
                  <span title={manufacturers.join(', ')}>
                    {manufacturers.slice(0, 3).join(', ')} +{manufacturers.length - 3} more
                  </span>
                );
              }}
            ></Column>
            <Column
              field='medications'
              header='Medications'
              style={{ minWidth: '20rem' }}
              filter
              filterElement={medicationsFilterElement}
              showFilterMatchModes={false}
              showFilterOperator={false}
              showAddButton={false}
              filterMatchMode='custom'
              filterFunction={(value, rowData) => {
                if (!value || value === '') return true
                const filterValue = String(value).toLowerCase().trim()
                if (!filterValue) return true
                const medications = rowData.medications || []
                if (medications.length === 0) return false
                return medications.some(medication => {
                  if (!medication) return false
                  const medicationString = String(medication).toLowerCase()
                  return medicationString.includes(filterValue)
                })
              }}
              body={(rowData) => {
                const medications = rowData.medications || [];
                if (medications.length === 0) {
                  return <span className='text-muted'>No medications</span>;
                }
                if (medications.length <= 2) {
                  return medications.join(', ');
                }
                return (
                  <span title={medications.join(', ')}>
                    {medications.slice(0, 2).join(', ')} +{medications.length - 2} more
                  </span>
                );
              }}
            ></Column>
            <Column
              field='upcs'
              header='UPCs'
              style={{ minWidth: '20rem' }}
              filter
              filterElement={upcsFilterElement}
              showFilterMatchModes={false}
              showFilterOperator={false}
              showAddButton={false}
              filterMatchMode='custom'
              filterFunction={(value, rowData) => {
                if (!value || value === '') return true
                const filterValue = String(value).toLowerCase().trim()
                if (!filterValue) return true
                const upcs = rowData.upcs || []
                if (upcs.length === 0) return false
                return upcs.some(upc => {
                  if (!upc) return false
                  const upcString = String(upc).toLowerCase()
                  return upcString.includes(filterValue)
                })
              }}
              body={(rowData) => {
                const upcs = rowData.upcs || [];
                if (upcs.length === 0) {
                  return <span className='text-muted'>No UPCs</span>;
                }
                if (upcs.length <= 3) {
                  return upcs.join(', ');
                }
                return (
                  <span title={upcs.join(', ')}>
                    {upcs.slice(0, 3).join(', ')} +{upcs.length - 3} more
                  </span>
                );
              }}
            ></Column>
            <Column
              body={actionBodyTemplate}
              exportable={false}
              style={{ minWidth: '12rem' }}
            ></Column>
          </DataTable>

          <ModifyMolecule ref={refs} getData={getData} />
        </div>
      </MainContentLayout>
    </>
  )
}
